<template>
  <div class="builder-view">
    <Notification group="wallet-edit" />

    <NavBar>
      <template #left>
        <BackButton @click="shouldGoBack" />
      </template>
      <template #center>{{ getNavBarTitle }}</template>
      <template v-if="showWalletContent" #right>
        <ButtonIconContextual
          data-testid="bt-deletar-produto"
          label="Deletar produto"
          aria-label="Deletar produto"
          :icon="isDeleteMode ? 'EF0030' : 'EE0010'"
          @click="handleDeleteMode"
        />
      </template>
    </NavBar>

    <component
      :is="WalletProviderComponent"
      v-slot="{ dataAllocations, productsQt, isMobile }"
      :portfolio-id="portfolioId"
      :is-partner="isPartner"
      :is-template="isTemplate"
      :data-testid="`is-editor-${isEditor}`"
    >
      <PreventUnsavedChangesModal
        :is-open="isOpenPreventUnsavedChangesModal"
        @close-prevent-unsaved-changes-modal="closePreventUnsavedChangesModal"
        @confirm-prevent-unsaved-changes-modal="
          confirmPreventUnsavedChangesModal
        "
      />
      <div v-if="!showWalletContent" class="builder-view__content">
        <div class="builder-view__content-container">
          <InstrumentsContainer
            is-scrollable
            :products-qt="productsQt"
            :selected-instrument-detail-id="instrumentId"
            :is-mobile="isMobile"
            :is-editor="isEditor"
            :allocations="dataAllocations"
            @handle-instrument-select="handleInstrumentSelect"
          />
        </div>
        <CheckoutBar
          title="Carteira"
          :summary-text="checkoutSummary(productsQt)"
          button-text="Próxima"
          :disable-input="productsQt === 0"
          @on-click-button="toggleShowWalletContent"
        />
      </div>
      <SelectedInstrumentsReview
        v-else
        :instruments="dataAllocations"
        :is-delete-active="isDeleteMode"
        :is-editor="isEditor"
        :portfolio-id="portfolioId"
        :is-template="isTemplate"
        @show-wallet-selection="toggleShowWalletContent"
        @disable-delete-mode="isDeleteMode = false"
      />
    </component>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { NavBar, ButtonIconContextual } from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons/BackButton';

import InstrumentsContainer from '../components/Instruments/InstrumentsContainer.vue';
import CheckoutBar from '@/modules/common/components/CheckoutBar/CheckoutBar.vue';
import PreventUnsavedChangesModal from '../components/PreventUnsavedChangesModal/PreventUnsavedChangesModal.vue';
import WalletCreateProvider from '../providers/WalletCreateProvider.vue';
import WalletEditProvider from '../providers/WalletEditProvider.vue';
import SelectedInstrumentsReview from '../components/Instruments/SelectedInstrumentsReview.vue';
import { Notification } from '@/modules/common/components/Notification';
import { EFilterCategoriesTypes } from '../services/types/models/IFilters';

const FilterModule = namespace('FilterModule');

@Component({
  components: {
    InstrumentsContainer,
    NavBar,
    ButtonIconContextual,
    PreventUnsavedChangesModal,
    WalletCreateProvider,
    WalletEditProvider,
    Notification,
    CheckoutBar,
    SelectedInstrumentsReview,
    BackButton
  }
})
export default class Builder extends Vue {
  @Prop({ type: String })
  public instrumentId?: string;

  @Prop({ type: Boolean, default: false })
  private isTemplate!: boolean;

  @Prop({ type: String })
  public portfolioId?: string;

  @Prop({ type: Boolean, default: false, required: false })
  public isPartner!: boolean;

  public showWalletContent = false;

  public isOpenPreventUnsavedChangesModal = false;

  public WalletProviderComponent?: WalletCreateProvider | WalletEditProvider;

  public isEditor = false;

  public isDeleteMode = false;

  public productSelectedToRemove = { name: '', type: '' };

  @FilterModule.Action('addFilterType')
  private addFilterType!: Function;

  public checkoutSummary(productsQt: number) {
    if (productsQt === 0) {
      return `Nenhum produto selecionado`;
    } else if (productsQt === 1) {
      return `${productsQt} produto selecionado`;
    } else {
      return `${productsQt} produtos selecionados`;
    }
  }

  beforeMount() {
    this.clearFilters();
    if (this.portfolioId) {
      this.isEditor = true;
      this.WalletProviderComponent =
        WalletEditProvider as unknown as WalletEditProvider;
    } else {
      this.isEditor = false;
      this.WalletProviderComponent =
        WalletCreateProvider as unknown as WalletCreateProvider;
    }
  }

  public handleDeleteMode() {
    this.isDeleteMode = !this.isDeleteMode;
  }

  public clearFilters() {
    this.addFilterType({
      filterType: {},
      categoryType: EFilterCategoriesTypes[EFilterCategoriesTypes.FIXEDINCOME]
    });

    this.addFilterType({
      filterType: {},
      categoryType:
        EFilterCategoriesTypes[EFilterCategoriesTypes.VARIABLEINCOME]
    });

    this.addFilterType({
      filterType: {},
      categoryType: EFilterCategoriesTypes[EFilterCategoriesTypes.OTHER]
    });
  }

  public shouldGoBack() {
    this.clearFilters();
    this.isOpenPreventUnsavedChangesModal = true;
  }

  public closePreventUnsavedChangesModal() {
    this.isOpenPreventUnsavedChangesModal = false;
  }

  public confirmPreventUnsavedChangesModal() {
    this.$router.back();
  }

  public get getNavBarTitle() {
    if (this.isDeleteMode) return 'Remover produtos';
    if (this.showWalletContent) return '';

    return this.isEditor ? 'Editar Alocação' : 'Nova Carteira';
  }

  public toggleShowWalletContent() {
    this.isDeleteMode = false;
    this.showWalletContent = !this.showWalletContent;
  }

  public handleInstrumentSelect(apiId: string) {
    const { query, name } = this.$route;

    if (apiId) {
      this.$router.replace({
        name: name!,
        query: { ...query, instrumentId: apiId }
      });
    } else {
      const { instrumentId, ...otherParams } = query;
      if (instrumentId) {
        this.$router.replace({
          name: name!,
          query: Object.assign({}, otherParams)
        });
      }
    }
  }
}
</script>
<style lang="less" scoped>
.builder-view {
  &__content {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-areas: 'wallet';

    @media (max-width: 1280px) {
      margin-bottom: @size-spacing-x1400;
    }

    &-active-list & {
      &-container {
        display: block;
      }
    }
    &-container {
      padding: @size-spacing-x400;
      display: flex;
      justify-content: center;
    }
  }
}
</style>
